import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { DISCIPLINE_CODES, LEAD_BASED_PAINT, STUDENT_CODE_TYPES } from '@shared/utils/app-static-data';
import { ConfirmationDialogueComponent } from 'app/components/common/confirmation-dialogue/confirmation-dialogue.component';
import { IAtpCourseStudent } from 'app/models/atp/atp-course-student';
import { IAtpPerson } from 'app/models/atp/atp-person';
import { IAccountAffiliation } from 'app/models/common/account-affiliation';
import { IIndividualAddress } from 'app/models/individuals/individual-address';
import { IIndividualApplication } from 'app/models/individuals/individual-application';
import { IIndividualCertificate } from 'app/models/individuals/individual-certificate';
import { IIndividualContactInformation } from 'app/models/individuals/individual-contact-information';
import { IIndividualTesting } from 'app/models/individuals/individual-testing';
import { IIndividualTraining } from 'app/models/individuals/individual-training';
import { IIndividualWorkspace } from 'app/models/individuals/individual-workspace';
import { IIndividual } from 'app/models/individuals/individual';
import { SharedService } from 'app/services/core/shared.service';
import { IndividualService } from 'app/services/individuals/individual.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PUBLIC_FIRM_ADDRESS_TYPES, PUBLIC_FIRM_CONTACT_TYPES } from '../../firms/firm-edit/firm-edit.constants';
import { IndividualApplicationComponent } from '../individual-application/individual-application.component';
import { IndividualAddAffiliationDialogComponent } from './individual-add-affiliation-dialog.component';
import { IndividualEditAddressDialogComponent } from './individual-edit-address-dialog.component';
import { IndividualEditContactInformationDialogComponent } from './individual-edit-contact-information-dialog.component';
import { IFirmPerson } from 'app/models/firm/firm-person';

interface IAddressRow {
  original: IIndividualAddress;
  id: number;
  contactType: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  isCurrentPhysical: boolean;
  isCurrentMailing: boolean;
}

interface IContactInformationRow {
  original: IIndividualContactInformation;
  id: number;
  contactType: string;
  label: string;
  phone: string;
  cell: string;
  fax: string;
  email: string;
  website: string;
}

interface IAffiliationRow {
  id: number;
  name: string;
  type: string;
  affiliationId?: number;
  requestStatus?: 'Pending' | 'Denied' | null;
  isRequestOnly?: boolean;
}

interface IAffiliationOption {
  id: number;
  name: string;
  type: 'ATP' | 'Firm';
  isPrivate: boolean;
}

interface IUserAccountRow extends IAccountAffiliation {
  userID: string;
}

@Component({
  selector: 'app-individual-edit',
  templateUrl: './individual-edit.component.html',
  styleUrls: ['./individual-edit.component.scss']
})
export class IndividualEditComponent implements OnInit {
  public isMobile = false;
  public loading = true;
  public saving = false;
  public didPersistChange = false;
  public showHistory = false;
  public selectedTabIndex = 0;
  public uiData = LEAD_BASED_PAINT;
  public workspace: IIndividualWorkspace | null = null;
  public individual: IIndividual | null = null;
  public personId = 0;
  public personForm!: UntypedFormGroup;
  public visibleApplicationCount = 0;

  public personCertificates: IIndividualCertificate[] = [];
  public personFilteredCertificates: IIndividualCertificate[] = [];
  public certificateFilterString = '';
  public certificateStageFilter = new FormControl<string>('Both', { nonNullable: true });
  public certificatesDataSource!: MatTableDataSource<any>;
  public certificateTable = {
    layout: {
      columns: ['codeType', 'stage', 'certificateNumber', 'controlNumber', 'issueDate', 'expirationDate', 'dateOfLetter'],
      container: [
        { displayName: 'Discipline', columnName: 'codeType', type: 'string', size: '16' },
        { displayName: 'Stage', columnName: 'stage', type: 'string', size: '10' },
        { displayName: 'Certificate Number', columnName: 'certificateNumber', type: 'string', size: '18' },
        { displayName: 'Control Number', columnName: 'controlNumber', type: 'string', size: '16' },
        { displayName: 'Issue Date', columnName: 'issueDate', type: 'date', size: '14' },
        { displayName: 'Expiration Date', columnName: 'expirationDate', type: 'date', size: '14' },
        { displayName: 'Date of Letter', columnName: 'dateOfLetter', type: 'date', size: '12' },
      ],
    }
  };
  public certificateTablePageSize = 5;
  @ViewChild('CertificateTablePaginator') certificatePaginator!: MatPaginator;
  @ViewChild('CertificateTableSort') certificateSort!: MatSort;

  public addressTableColumns = ['contactType', 'street', 'city', 'state', 'zip', 'isCurrentPhysical', 'isCurrentMailing', 'actions'];
  public addressTableFormat = [
    { displayName: 'Type', columnName: 'contactType', type: 'string', size: '11' },
    { displayName: 'Street', columnName: 'street', type: 'string', size: '24' },
    { displayName: 'City', columnName: 'city', type: 'string', size: '15' },
    { displayName: 'State', columnName: 'state', type: 'string', size: '8' },
    { displayName: 'Zip', columnName: 'zip', type: 'string', size: '8' },
    { displayName: 'Current Physical', columnName: 'isCurrentPhysical', type: 'bool', size: '13' },
    { displayName: 'Current Mailing', columnName: 'isCurrentMailing', type: 'bool', size: '13' },
  ];
  public addressTableData: IAddressRow[] = [];
  public addressFilteredTableData: IAddressRow[] = [];
  public addressTablePageSize = 5;
  public filterAddressString = '';
  public filterAddressType = new FormControl<string[]>([], { nonNullable: true });
  public filterAddressPhysical = new FormControl<string[]>([], { nonNullable: true });
  public filterAddressMailing = new FormControl<string[]>([], { nonNullable: true });
  public addressTypeList = Object.values(PUBLIC_FIRM_ADDRESS_TYPES);
  public addressBoolList: string[] = ['Unchecked', 'Checked'];
  public addressDataSource!: MatTableDataSource<any>;
  @ViewChild('AddressTablePaginator') addressPaginator!: MatPaginator;
  @ViewChild('AddressTableSort') addressSort!: MatSort;

  public contactInformationTableColumns = ['contactType', 'label', 'phone', 'cell', 'fax', 'email', 'website', 'actions'];
  public contactInformationTableFormat = [
    { displayName: 'Type', columnName: 'contactType', type: 'string', size: '10' },
    { displayName: 'Name', columnName: 'label', type: 'string', size: '14' },
    { displayName: 'Phone', columnName: 'phone', type: 'string', size: '12' },
    { displayName: 'Cell', columnName: 'cell', type: 'string', size: '12' },
    { displayName: 'Fax', columnName: 'fax', type: 'string', size: '10' },
    { displayName: 'Email', columnName: 'email', type: 'string', size: '18' },
    { displayName: 'Website', columnName: 'website', type: 'string', size: '17' },
  ];
  public contactInformationTableData: IContactInformationRow[] = [];
  public contactInformationFilteredTableData: IContactInformationRow[] = [];
  public contactInformationTablePageSize = 5;
  public filterContactInformationString = '';
  public filterContactInformationType = new FormControl<string[]>([], { nonNullable: true });
  public contactInformationTypeList = Object.values(PUBLIC_FIRM_CONTACT_TYPES);
  public contactInformationDataSource!: MatTableDataSource<any>;
  @ViewChild('ContactInformationTablePaginator') contactInformationPaginator!: MatPaginator;
  @ViewChild('ContactInformationTableSort') contactInformationSort!: MatSort;

  public testingTableColumns = ['codeType', 'testDate', 'testSerial', 'testScore', 'passFail'];
  public testingTableFormat = [
    { displayName: 'Discipline', columnName: 'codeType', type: 'string', size: '28' },
    { displayName: 'Test Date', columnName: 'testDate', type: 'date', size: '16' },
    { displayName: 'Test Serial#', columnName: 'testSerial', type: 'string', size: '22' },
    { displayName: 'Test Score', columnName: 'testScore', type: 'string', size: '17' },
    { displayName: 'Pass/Fail', columnName: 'passFail', type: 'string', size: '17' },
  ];
  public testingTableData: IIndividualTesting[] = [];
  public testingFilteredTableData: IIndividualTesting[] = [];
  public testingTablePageSize = 5;
  public testingFilterString = '';
  public testingDataSource!: MatTableDataSource<any>;
  @ViewChild('TestingTablePaginator') testingPaginator!: MatPaginator;
  @ViewChild('TestingTableSort') testingSort!: MatSort;

  public trainingTableColumns = ['discipline', 'courseStart', 'courseEnd', 'testScore', 'courseCertificateNumber'];
  public trainingTableFormat = [
    { displayName: 'Discipline', columnName: 'discipline', type: 'string', size: '28' },
    { displayName: 'Start Date', columnName: 'courseStart', type: 'date', size: '16' },
    { displayName: 'End Date', columnName: 'courseEnd', type: 'date', size: '16' },
    { displayName: 'Test Score', columnName: 'testScore', type: 'string', size: '15' },
    { displayName: 'Certificate Number', columnName: 'courseCertificateNumber', type: 'string', size: '25' },
  ];
  public trainingTableData: Array<IAtpCourseStudent & { discipline: string; courseStart: string | null; courseEnd: string | null }> = [];
  public trainingFilteredTableData: Array<IAtpCourseStudent & { discipline: string; courseStart: string | null; courseEnd: string | null }> = [];
  public trainingTablePageSize = 5;
  public trainingFilterString = '';
  public trainingDataSource!: MatTableDataSource<any>;
  @ViewChild('TrainingTablePaginator') trainingPaginator!: MatPaginator;
  @ViewChild('TrainingTableSort') trainingSort!: MatSort;

  public affiliationTable = {
    layout: {
      columns: ['name', 'type', 'status'],
      container: [
        { displayName: 'Affiliation Name', columnName: 'name', type: 'string', size: '46' },
        { displayName: 'Affiliation Type', columnName: 'type', type: 'string', size: '20' },
        { displayName: 'Status', columnName: 'status', type: 'string', size: '19' },
      ],
    }
  };
  public personAffiliations: IAffiliationRow[] = [];
  public personFilteredAffiliations: IAffiliationRow[] = [];
  public affiliationFilterString = '';
  public affiliationTablePageSize = 5;
  public affiliationDataSource!: MatTableDataSource<any>;
  @ViewChild('AffiliationTablePaginator') affiliationPaginator!: MatPaginator;
  @ViewChild('AffiliationTableSort') affiliationSort!: MatSort;

  public userAccounts: IUserAccountRow[] = [];
  public userAccountsDataSource!: MatTableDataSource<any>;
  public userAccountTable = {
    layout: {
      columns: ['userName', 'userID', 'status', 'requestedStatus', 'lastUpdatedBy', 'actions'],
      container: [
        { displayName: 'User Name', columnName: 'userName', type: 'string', size: '23' },
        { displayName: 'User Email', columnName: 'userID', type: 'string', size: '25' },
        { displayName: 'Status', columnName: 'status', type: 'string', size: '15' },
        { displayName: 'Requested Status', columnName: 'requestedStatus', type: 'string', size: '15' },
        { displayName: 'Requested By', columnName: 'lastUpdatedBy', type: 'string', size: '15' },
      ],
      data: [] as IUserAccountRow[]
    }
  };
  public userAccountTablePageSize = 5;
  public filterUserAccountString = '';
  @ViewChild('UserAccountTablePaginator') userAccountPaginator!: MatPaginator;
  @ViewChild('UserAccountTableSort') userAccountSort!: MatSort;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { personId: number } | null,
    @Optional() private readonly dialogRef: MatDialogRef<IndividualEditComponent> | null,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly individualService: IndividualService,
    public readonly sharedService: SharedService,
    private readonly toastr: ToastrService,
  ) {
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe((result) => {
      this.isMobile = result.matches;
    });
  }

  ngOnInit(): void {
    this.initializeForms();

    if (this.data?.personId) {
      this.personId = this.data.personId;
      this.loadWorkspace();
      return;
    }

    this.route.queryParamMap.subscribe((params) => {
      const rawPersonId = params.get('personId');
      const nextPersonId = rawPersonId ? Number(rawPersonId) : NaN;

      if (!nextPersonId) {
        this.toastr.error('Individual not found.');
        this.router.navigate(['/individuals/management']);
        return;
      }

      this.personId = nextPersonId;
      this.loadWorkspace();
    });
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  initializeForms(): void {
    this.personForm = new UntypedFormGroup({
      response: new UntypedFormControl(''),
      inactive: new UntypedFormControl(false),
      firstName: new UntypedFormControl('', [Validators.required]),
      lastName: new UntypedFormControl('', [Validators.required]),
      middleName: new UntypedFormControl(''),
      title: new UntypedFormControl(''),
    });
  }

  loadWorkspace(): void {
    this.loading = true;
    forkJoin({
      workspace: this.individualService.getIndividualWorkspace(this.personId),
      trainingHistory: this.individualService.getIndividualTrainingHistory(this.personId).pipe(catchError(() => of([]))),
      firmAffiliations: this.individualService.getIndividualFirmAffiliations(this.personId).pipe(catchError(() => of([]))),
      atpAffiliations: this.individualService.getIndividualAtpAffiliations(this.personId).pipe(catchError(() => of([]))),
      userAccounts: this.individualService.getIndividualUserAccounts(this.personId).pipe(catchError(() => of([]))),
    }).subscribe({
      next: (result) => {
        this.workspace = result.workspace;
        this.individual = result.workspace.individual;
        this.visibleApplicationCount = result.workspace.applications.length;
        this.fillPersonForm();
        this.loadAddressData(result.workspace);
        this.loadContactInformationData(result.workspace);
        this.loadCertificateData(result.workspace);
        this.loadTestingData(result.workspace);
        this.loadTrainingData(result.trainingHistory);
        this.loadAffiliationData(result.workspace, result.firmAffiliations, result.atpAffiliations);
        this.loadUserAccounts(result.userAccounts);
        this.loading = false;
      },
      error: (error) => {
        this.toastr.error('Error loading Individual workspace: ' + (error?.message || error?.error || 'Unknown error'));
        this.loading = false;
      }
    });
  }

  fillPersonForm(): void {
    if (!this.individual) {
      return;
    }

    for (const controlName in this.individual) {
      if (Object.prototype.hasOwnProperty.call(this.personForm.controls, controlName)) {
        this.personForm.controls[controlName].setValue(this.individual[controlName as keyof IIndividual]);
      }
    }
  }

  loadAddressData(workspace: IIndividualWorkspace): void {
    this.addressTableData = (workspace.addresses || []).map((address) => ({
      original: address,
      id: address.id,
      contactType: address.address?.contactType || '',
      street: address.address?.street || '',
      city: address.address?.city || '',
      state: address.address?.state || '',
      zip: address.address?.zip || '',
      isCurrentPhysical: address.isCurrentPhysical === true,
      isCurrentMailing: address.isCurrentMailing === true,
    })).sort((left, right) => {
      const leftIsCurrent = left.isCurrentPhysical || left.isCurrentMailing;
      const rightIsCurrent = right.isCurrentPhysical || right.isCurrentMailing;

      if (leftIsCurrent && !rightIsCurrent) {
        return -1;
      }

      if (!leftIsCurrent && rightIsCurrent) {
        return 1;
      }

      return 0;
    });
    this.filterAddressTable();
  }

  loadContactInformationData(workspace: IIndividualWorkspace): void {
    this.contactInformationTableData = (workspace.contactInformations || []).map((contact) => ({
      original: contact,
      id: contact.id,
      contactType: contact.contactInformation?.contactType || '',
      label: contact.contactInformation?.label || '',
      phone: contact.contactInformation?.phone || '',
      cell: contact.contactInformation?.cell || '',
      fax: contact.contactInformation?.fax || '',
      email: contact.contactInformation?.email || '',
      website: contact.contactInformation?.website || '',
    }));
    this.filterContactInformationTable();
  }

  loadCertificateData(workspace: IIndividualWorkspace): void {
    this.personCertificates = workspace.certificates || [];
    this.filterCertificateTable();
  }

  loadTestingData(workspace: IIndividualWorkspace): void {
    this.testingTableData = (workspace.testings || []).map((testing) => ({
      ...testing,
      codeType: this.normalizeDisciplineDisplay(testing.codeType),
    }));
    this.filterTestingTable();
  }

  loadTrainingData(trainingHistory: IAtpCourseStudent[]): void {
    this.trainingTableData = (trainingHistory || []).map((training) => ({
      ...training,
      discipline: this.normalizeDisciplineDisplay(training.atpCourse?.accreditationDiscipline || ''),
      courseStart: training.atpCourse?.courseStart || null,
      courseEnd: training.atpCourse?.courseEnd || null,
    }));
    this.filterTrainingTable();
  }

  loadAffiliationData(workspace: IIndividualWorkspace, firmAffiliations: IFirmPerson[], atpAffiliations: IAtpPerson[]): void {
    const rows: IAffiliationRow[] = [];

    (firmAffiliations || []).forEach((affiliation) => {
      if (affiliation?.firm) {
        rows.push({
          affiliationId: affiliation.id,
          id: affiliation.firm.id,
          type: 'Firm',
          name: affiliation.firm.name || '',
          requestStatus: this.normalizeAffiliationRequestStatus((affiliation as any).requestStatus),
          isRequestOnly: (affiliation as any).isRequestOnly === true,
        });
      }
    });

    (atpAffiliations || []).forEach((affiliation) => {
      if (affiliation?.atp) {
        rows.push({
          affiliationId: affiliation.id,
          id: affiliation.atp.id,
          type: 'ATP',
          name: affiliation.atp.name || '',
          requestStatus: this.normalizeAffiliationRequestStatus((affiliation as any).requestStatus),
          isRequestOnly: (affiliation as any).isRequestOnly === true,
        });
      }
    });

    if (!rows.length) {
      this.personAffiliations = (workspace.affiliations || []).map((affiliation, index) => {
        const parts = affiliation.split(':');
        return {
          id: index + 1,
          type: parts[0] || 'Affiliation',
          name: parts.length > 1 ? parts.slice(1).join(':') : affiliation,
          affiliationId: undefined,
          requestStatus: null,
          isRequestOnly: false,
        };
      });
    } else {
      this.personAffiliations = rows.sort((left, right) => {
        const typeCompare = left.type.localeCompare(right.type);
        if (typeCompare !== 0) {
          return typeCompare;
        }

        return left.name.localeCompare(right.name);
      });
    }

    this.filterAffiliationTable();
  }

  loadUserAccounts(accounts: IAccountAffiliation[]): void {
    this.userAccounts = (accounts || []).map((account) => {
      const userIdentifier = account.userId || (account as any).userID || '';
      return {
        ...account,
        userId: userIdentifier,
        userID: userIdentifier,
      };
    });
    this.userAccountTable.layout.data = this.userAccounts;
    this.updateUserAccountDataSource();
  }

  updateResponse(event: any): void {
    this.personForm.get('response')?.setValue(event.checked ? 'Yes' : 'No');
  }

  saveChanges(): void {
    if (!this.individual) {
      return;
    }

    const updatedIndividual: Pick<IIndividual, 'id' | 'firstName' | 'middleName' | 'lastName' | 'title' | 'inactive' | 'response'> = {
      id: this.individual.id,
      response: this.personForm.get('response')?.value,
      inactive: this.personForm.get('inactive')?.value,
      firstName: this.personForm.get('firstName')?.value,
      lastName: this.personForm.get('lastName')?.value,
      middleName: this.personForm.get('middleName')?.value,
      title: this.personForm.get('title')?.value,
    };

    this.saving = true;
    this.individualService.saveIndividual(updatedIndividual).subscribe({
      next: (savedIndividual) => {
        this.individual = savedIndividual;
        this.didPersistChange = true;
        this.saving = false;
        this.fillPersonForm();
        this.toastr.success('Individual information saved successfully!');
      },
      error: (error) => {
        this.saving = false;
        this.toastr.error('Error saving Individual information: ' + (error?.message || error?.error || 'Unknown error'));
      }
    });
  }

  exitComponent(): void {
    if (this.dialogRef) {
      this.dialogRef.close(this.didPersistChange);
      return;
    }

    this.router.navigate(['/individuals/management']);
  }

  onApplicationCountChanged(count: number): void {
    this.visibleApplicationCount = count;
  }

  viewApplication(application?: IIndividualApplication | null, certificate?: IIndividualCertificate | null): void {
    if (application?.id) {
      this.openApplication(application.id);
      return;
    }

    if (certificate?.controlNumber) {
      this.openCertificateAction(certificate);
      return;
    }

    const dialogRef = this.dialog.open(IndividualApplicationComponent, {
      width: '95%',
      autoFocus: false,
      data: { personId: this.personId, workflow: 'new' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadWorkspace();
      }
    });
  }

  openRenewalApplication(previousApplication?: string | null): void {
    const dialogRef = this.dialog.open(IndividualApplicationComponent, {
      width: '95%',
      autoFocus: false,
      data: {
        workflow: 'renewal',
        personId: this.personId,
        previousApplication: previousApplication || null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.didPersistChange = true;
        this.loadWorkspace();
      }
    });
  }

  openCertificateAction(row: IIndividualCertificate): void {
    const pendingApplication = this.getPendingRenewalApplication(row.controlNumber || null);
    if (pendingApplication) {
      this.openApplication(pendingApplication.id);
      return;
    }

    this.openRenewalApplication(row.controlNumber || null);
  }

  getCertificateActionLabel(certificate: IIndividualCertificate): string {
    return this.getPendingRenewalApplication(certificate.controlNumber || null) ? 'View' : 'Renew';
  }

  addressCheckboxChange(row: IAddressRow, fieldName: string): void {
    if (fieldName !== 'isCurrentPhysical' && fieldName !== 'isCurrentMailing') {
      return;
    }

    const previousSelection = this.addressTableData.map((address) => ({
      id: address.id,
      value: address[fieldName],
    }));

    const nextValue = !row[fieldName];
    if (nextValue) {
      this.addressTableData.forEach((address) => {
        if (address.id === row.id) {
          return;
        }

        address[fieldName] = false;
        address.original[fieldName] = false;
      });
    }

    row[fieldName] = nextValue;
    row.original[fieldName] = nextValue;
    this.filterAddressTable();

    this.individualService.saveIndividualAddress(row.original).subscribe({
      next: (savedAddress) => {
        const addressIndex = this.addressTableData.findIndex((address) => address.id === row.id);
        if (addressIndex >= 0) {
          this.addressTableData[addressIndex] = {
            original: savedAddress,
            id: savedAddress.id,
            contactType: savedAddress.address?.contactType || '',
            street: savedAddress.address?.street || '',
            city: savedAddress.address?.city || '',
            state: savedAddress.address?.state || '',
            zip: savedAddress.address?.zip || '',
            isCurrentPhysical: savedAddress.isCurrentPhysical === true,
            isCurrentMailing: savedAddress.isCurrentMailing === true,
          };
        }

        if (savedAddress[fieldName] === true) {
          this.addressTableData.forEach((address) => {
            if (address.id === savedAddress.id) {
              return;
            }

            address[fieldName] = false;
            address.original[fieldName] = false;
          });
        }

        this.addressTableData.sort((left, right) => {
          const leftIsCurrent = left.isCurrentPhysical || left.isCurrentMailing;
          const rightIsCurrent = right.isCurrentPhysical || right.isCurrentMailing;

          if (leftIsCurrent && !rightIsCurrent) {
            return -1;
          }

          if (!leftIsCurrent && rightIsCurrent) {
            return 1;
          }

          return 0;
        });

        this.didPersistChange = true;
        this.toastr.success('Current Address Updated');
        this.filterAddressTable();
      },
      error: (error) => {
        this.addressTableData.forEach((address) => {
          const previous = previousSelection.find((entry) => entry.id === address.id);
          if (!previous) {
            return;
          }

          address[fieldName] = previous.value;
          address.original[fieldName] = previous.value;
        });

        this.filterAddressTable();
        this.toastr.error('An error occurred saving the address: ' + (error?.message || error?.error || 'Unknown error'));
      }
    });
  }

  editAddressTableEntry(row: IAddressRow | null): void {
    const dialogRef = this.dialog.open(IndividualEditAddressDialogComponent, {
      width: this.sharedService.isMobile ? '95%' : '520px',
      autoFocus: false,
      data: {
        address: row?.original ?? {
          id: 0,
          individualId: this.personId,
          address: { id: 0, street: '', city: '', state: '', zip: '', county: '', contactType: '' },
          isCurrentPhysical: false,
          isCurrentMailing: false,
        },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadWorkspace();
      }
    });
  }

  deleteAddressEntry(row: IAddressRow): void {
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to <strong>Delete</strong> this Address?',
        title: 'Delete Address',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.individualService.deleteIndividualAddress(row.original.id).subscribe({
        next: () => {
          this.didPersistChange = true;
          this.loadWorkspace();
        },
        error: (error) => {
          this.toastr.error('Error deleting Address: ' + (error?.message || error?.error || 'Unknown error'));
        }
      });
    });
  }

  editContactInformationTableEntry(row: IContactInformationRow | null): void {
    const dialogRef = this.dialog.open(IndividualEditContactInformationDialogComponent, {
      width: this.sharedService.isMobile ? '95%' : '520px',
      autoFocus: false,
      data: {
        contactInformation: row?.original ?? {
          id: 0,
          individualId: this.personId,
          contactInformation: { id: 0, phone: '', ext: '', cell: '', fax: '', email: '', website: '', contactType: '', label: '' },
        },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadWorkspace();
      }
    });
  }

  deleteContactInformationEntry(row: IContactInformationRow): void {
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to <strong>Delete</strong> this Contact Information?',
        title: 'Delete Contact Information',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.individualService.deleteIndividualContactInformation(row.original.id).subscribe({
        next: () => {
          this.didPersistChange = true;
          this.loadWorkspace();
        },
        error: (error) => {
          this.toastr.error('Error deleting Contact Information: ' + (error?.message || error?.error || 'Unknown error'));
        }
      });
    });
  }

  filterAddressTable(): void {
    const filterValue = this.filterAddressString.trim().toLowerCase();
    this.addressFilteredTableData = this.addressTableData.filter((address) => {
      const matchesText = !filterValue || [address.id, address.street, address.city, address.state, address.zip].join(' ').toLowerCase().includes(filterValue);
      const matchesType = !this.filterAddressType.value.length || this.filterAddressType.value.includes(address.contactType);
      const matchesPhysical = !this.filterAddressPhysical.value.length || this.filterAddressPhysical.value.includes(address.isCurrentPhysical ? 'Checked' : 'Unchecked');
      const matchesMailing = !this.filterAddressMailing.value.length || this.filterAddressMailing.value.includes(address.isCurrentMailing ? 'Checked' : 'Unchecked');
      return matchesText && matchesType && matchesPhysical && matchesMailing;
    });
    this.updateAddressDataSource();
  }

  updateAddressDataSource(): void {
    this.addressDataSource = new MatTableDataSource<any>(this.addressFilteredTableData);
    setTimeout(() => {
      if (this.addressPaginator) {
        this.addressDataSource.paginator = this.addressPaginator;
      }
      if (this.addressSort) {
        this.addressDataSource.sort = this.addressSort;
      }
    });
  }

  filterContactInformationTable(): void {
    const filterValue = this.filterContactInformationString.trim().toLowerCase();
    this.contactInformationFilteredTableData = this.contactInformationTableData.filter((contact) => {
      const matchesText = !filterValue || [contact.id, contact.label, contact.phone, contact.cell, contact.fax, contact.email, contact.website].join(' ').toLowerCase().includes(filterValue);
      const matchesType = !this.filterContactInformationType.value.length || this.filterContactInformationType.value.includes(contact.contactType);
      return matchesText && matchesType;
    });
    this.updateContactInformationDataSource();
  }

  updateContactInformationDataSource(): void {
    this.contactInformationDataSource = new MatTableDataSource<any>(this.contactInformationFilteredTableData);
    setTimeout(() => {
      if (this.contactInformationPaginator) {
        this.contactInformationDataSource.paginator = this.contactInformationPaginator;
      }
      if (this.contactInformationSort) {
        this.contactInformationDataSource.sort = this.contactInformationSort;
      }
    });
  }

  filterCertificateTable(): void {
    const filterValue = this.certificateFilterString.trim().toLowerCase();
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    this.personFilteredCertificates = this.personCertificates.filter((certificate) => {
      const matchesText = !filterValue || [
        certificate.id,
        certificate.codeType,
        certificate.stage,
        certificate.certificateNumber,
        certificate.controlNumber,
      ].join(' ').toLowerCase().includes(filterValue);

      const stageValue = this.certificateStageFilter.value;
      const matchesStage = stageValue === 'Both' || certificate.stage === stageValue;
      const expirationDate = certificate.expirationDate ? new Date(certificate.expirationDate) : null;
      const matchesHistory = this.showHistory || !expirationDate || expirationDate >= currentDate;
      return matchesText && matchesStage && matchesHistory;
    });

    this.certificatesDataSource = new MatTableDataSource<any>(this.personFilteredCertificates);
    setTimeout(() => {
      if (this.certificatePaginator) {
        this.certificatesDataSource.paginator = this.certificatePaginator;
      }
      if (this.certificateSort) {
        this.certificatesDataSource.sort = this.certificateSort;
      }
    });
  }

  filterTestingTable(): void {
    const filterValue = this.testingFilterString.trim().toLowerCase();
    this.testingFilteredTableData = this.testingTableData.filter((testing) =>
      !filterValue || [testing.id, testing.codeType, testing.testSerial, testing.testScore, testing.passFail].join(' ').toLowerCase().includes(filterValue)
    );
    this.testingDataSource = new MatTableDataSource<any>(this.testingFilteredTableData);
    setTimeout(() => {
      if (this.testingPaginator) {
        this.testingDataSource.paginator = this.testingPaginator;
      }
      if (this.testingSort) {
        this.testingDataSource.sort = this.testingSort;
      }
    });
  }

  filterTrainingTable(): void {
    const filterValue = this.trainingFilterString.trim().toLowerCase();
    this.trainingFilteredTableData = this.trainingTableData.filter((training) =>
      !filterValue || [training.id, training.discipline, training.courseCertificateNumber, training.testScore].join(' ').toLowerCase().includes(filterValue)
    );
    this.trainingDataSource = new MatTableDataSource<any>(this.trainingFilteredTableData);
    setTimeout(() => {
      if (this.trainingPaginator) {
        this.trainingDataSource.paginator = this.trainingPaginator;
      }
      if (this.trainingSort) {
        this.trainingDataSource.sort = this.trainingSort;
      }
    });
  }

  filterAffiliationTable(): void {
    const filterValue = this.affiliationFilterString.trim().toLowerCase();
    this.personFilteredAffiliations = this.personAffiliations.filter((affiliation) =>
      !filterValue || [affiliation.id, affiliation.name, affiliation.type].join(' ').toLowerCase().includes(filterValue)
    );
    this.affiliationDataSource = new MatTableDataSource<any>(this.personFilteredAffiliations);
    setTimeout(() => {
      if (this.affiliationPaginator) {
        this.affiliationDataSource.paginator = this.affiliationPaginator;
      }
      if (this.affiliationSort) {
        this.affiliationDataSource.sort = this.affiliationSort;
      }
    });
  }

  applyUserAccountFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filterUserAccountString = filterValue;

    if (!this.userAccountsDataSource) {
      return;
    }

    this.userAccountsDataSource.filter = filterValue.trim().toLowerCase();

    if (this.userAccountsDataSource.paginator) {
      this.userAccountsDataSource.paginator.firstPage();
    }
  }

  updateUserAccountDataSource(): void {
    const sortedAccounts = [...this.userAccounts].sort((left, right) => {
      const leftHasPendingRequest = left.status !== left.requestedStatus;
      const rightHasPendingRequest = right.status !== right.requestedStatus;
      const leftIsInactive = left.status === 'Inactive' && left.requestedStatus === 'Inactive';
      const rightIsInactive = right.status === 'Inactive' && right.requestedStatus === 'Inactive';

      if (leftHasPendingRequest && !rightHasPendingRequest) {
        return -1;
      }

      if (!leftHasPendingRequest && rightHasPendingRequest) {
        return 1;
      }

      if (!leftHasPendingRequest && !rightHasPendingRequest) {
        if (leftIsInactive && !rightIsInactive) {
          return 1;
        }

        if (!leftIsInactive && rightIsInactive) {
          return -1;
        }
      }

      return 0;
    });

    this.userAccountsDataSource = new MatTableDataSource<any>(sortedAccounts);
    this.userAccountsDataSource.paginator = this.userAccountPaginator;
    this.userAccountsDataSource.sort = this.userAccountSort;
    this.userAccountsDataSource.filter = this.filterUserAccountString.trim().toLowerCase();
  }

  getRowClass(row: IUserAccountRow): string {
    if (row.status !== row.requestedStatus) {
      return 'pending-request-row';
    }

    if (row.status === 'Inactive') {
      return 'inactive-row';
    }

    return '';
  }

  shouldShowRequestedBy(row: IUserAccountRow): boolean {
    return row.status !== row.requestedStatus;
  }

  toggleUserAccountRequestedStatus(row: IUserAccountRow): void {
    const newRequestedStatus = row.requestedStatus === 'Active' ? 'Inactive' : 'Active';
    const action = newRequestedStatus === 'Active' ? 'Activate' : 'Deactivate';

    const affiliation: IAccountAffiliation = {
      id: row.id,
      userId: row.userId || row.userID,
      firm: row.firm,
      atp: row.atp,
      status: row.status,
      requestedStatus: newRequestedStatus,
      person: row.person,
      lastUpdatedBy: row.lastUpdatedBy,
      userName: row.userName,
    };

    this.individualService.saveUserAccountAffiliation(affiliation).subscribe({
      next: () => {
        this.didPersistChange = true;
        this.toastr.success(action + ' request submitted successfully');
        this.loadWorkspace();
      },
      error: (error) => {
        this.toastr.error('Error submitting ' + action.toLowerCase() + ' request: ' + (error?.message || error?.error || 'Unknown error'));
      }
    });
  }

  deleteAffiliation(row: IAffiliationRow): void {
    const isSupportedAffiliation = row.type === 'ATP' || row.type === 'Firm';

    if (!isSupportedAffiliation) {
      this.toastr.info('This affiliation cannot be removed from the individual workspace.');
      return;
    }

    const isRequestRow = this.isRequestAffiliation(row);
    if (!isRequestRow && !row.affiliationId) {
      this.toastr.info('This affiliation cannot be removed from the individual workspace.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to remove the ' + row.type + ' affiliation: <strong>' + row.name + '</strong>?',
        title: 'Delete ' + row.type + ' Affiliation',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      const deleteRequest = isRequestRow
        ? this.individualService.cancelIndividualAffiliationRequest(this.personId, row.type as 'ATP' | 'Firm', row.id)
        : (row.type === 'ATP'
          ? this.individualService.deleteIndividualAtpAffiliation(row.affiliationId as number)
          : this.individualService.deleteIndividualFirmAffiliation(row.affiliationId as number));

      deleteRequest.subscribe({
        next: (result: any) => {
          if (isRequestRow && result?.success === false) {
            this.toastr.info('The affiliation request for ' + row.name + ' was already processed.');
            this.loadWorkspace();
            return;
          }

          this.didPersistChange = true;
          this.toastr.success(
            isRequestRow
              ? 'The affiliation request for ' + row.name + ' was cancelled.'
              : 'The affiliation: ' + row.name + ' was successfully removed.'
          );
          this.loadWorkspace();
        },
        error: (error) => {
          this.toastr.error('Failed to remove the affiliation: ' + (error?.message || error?.error || 'Unknown error'));
        }
      });
    });
  }

  openAddAffiliationDialog(): void {
    if (!this.individual?.id) {
      return;
    }

    const dialogRef = this.dialog.open(IndividualAddAffiliationDialogComponent, {
      width: this.sharedService.isMobile ? '95%' : '560px',
      maxHeight: this.sharedService.isMobile ? '72vh' : '60vh',
      autoFocus: false,
      data: {
        existingAffiliations: this.personAffiliations
          .filter((affiliation) => affiliation.requestStatus !== 'Denied')
          .map((affiliation) => ({
            id: affiliation.id,
            type: affiliation.type,
          })),
      },
    });

    dialogRef.afterClosed().subscribe((result: IAffiliationOption | null) => {
      if (!result || !this.individual?.id) {
        return;
      }

      const saveRequest = result.type === 'ATP'
        ? this.individualService.requestIndividualAffiliation(this.individual.id, 'ATP', result.id)
        : this.individualService.requestIndividualAffiliation(this.individual.id, 'Firm', result.id);

      saveRequest.subscribe({
        next: () => {
          this.didPersistChange = true;
          this.toastr.success(result.type + ' affiliation request submitted.');
          this.loadWorkspace();
        },
        error: (error) => {
          this.toastr.error('Failed to add affiliation: ' + (error?.message || error?.error || 'Unknown error'));
        }
      });
    });
  }

  getAffiliationStatus(row: IAffiliationRow): string {
    return row.requestStatus || '';
  }

  canDeleteAffiliation(row: IAffiliationRow): boolean {
    const isSupportedAffiliation = row.type === 'ATP' || row.type === 'Firm';
    if (!isSupportedAffiliation) {
      return false;
    }

    return this.isRequestAffiliation(row) || !!row.affiliationId;
  }

  getAffiliationStatusClass(row: IAffiliationRow): string {
    if (row.requestStatus === 'Pending') {
      return 'affiliation-status-pending';
    }

    if (row.requestStatus === 'Denied') {
      return 'affiliation-status-denied';
    }

    return '';
  }

  getAffiliationRowClass(row: IAffiliationRow): string {
    return this.getAffiliationStatusClass(row);
  }

  displayLimit(text: any, limit: string): string {
    if (typeof text === 'string' && text.length > parseInt(limit, 10)) {
      return text.substring(0, parseInt(limit, 10)) + '...';
    }

    return text ?? '';
  }

  showBlockedFeature(message: string): void {
    this.toastr.info(message);
  }

  private getPendingRenewalApplication(controlNumber: string | null): IIndividualApplication | undefined {
    if (!controlNumber || !this.workspace) {
      return undefined;
    }

    return this.workspace.applications.find((application) =>
      application.previousApplication === controlNumber
      && application.status !== 'Approved'
      && application.status !== 'Cancelled'
    );
  }

  private openApplication(applicationId: number): void {
    const dialogRef = this.dialog.open(IndividualApplicationComponent, {
      width: '95%',
      autoFocus: false,
      data: { applicationId },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.didPersistChange = true;
        this.loadWorkspace();
      }
    });
  }

  private normalizeDisciplineDisplay(value: string | null | undefined): string {
    const rawValue = (value || '').trim();
    if (!rawValue) {
      return '';
    }

    const canonicalDisciplines = new Map<string, string>();

    Object.values(DISCIPLINE_CODES).forEach((discipline) => {
      canonicalDisciplines.set(discipline.code.toLowerCase(), discipline.display);
      canonicalDisciplines.set(discipline.display.toLowerCase(), discipline.display);
    });

    Object.values(STUDENT_CODE_TYPES).forEach((discipline) => {
      canonicalDisciplines.set(discipline.toLowerCase(), discipline);
    });

    canonicalDisciplines.set('risk assesor', 'Risk Assessor');

    return canonicalDisciplines.get(rawValue.toLowerCase()) || rawValue;
  }

  private normalizeAffiliationRequestStatus(value: any): 'Pending' | 'Denied' | null {
    const normalized = (value || '').toString().trim().toLowerCase();
    if (normalized === 'pending') {
      return 'Pending';
    }

    if (normalized === 'denied') {
      return 'Denied';
    }

    return null;
  }

  private isRequestAffiliation(row: IAffiliationRow): boolean {
    return row.requestStatus === 'Pending' || row.requestStatus === 'Denied' || row.isRequestOnly === true;
  }
}